/**
 * Query-Windowed Dataset Builder
 * One row per query — serves clustering, profiles, intervention, and within-session dynamics.
 */

import { getMessagesBetweenUserAndAI, AI_AGENT_ID } from '../../workspace/service/workspaceMessageService'

// ─── Helpers ─────────────────────────────────────────────────

function esc(v) { return `"${String(v ?? '').replace(/"/g, '""')}"` }

function classifyProfile(edits, runs, errors, hasPriorAi) {
  if (edits === 0 && runs === 0) return hasPriorAi ? 'Passive' : 'Cold Start'
  if (runs === 0 || (runs >= 1 && errors === 0)) return 'Moderate'
  if (runs >= 3 && errors >= 3) return 'Intensive'
  return 'Active'
}

function normalizeBehaviorId(id) {
  const map = {
    implementing: 'implementing', thinking: 'thinking', debugging: 'debugging',
    seekingHelp: 'seeking_help', seeking_help: 'seeking_help',
    idle: 'thinking', testing: 'testing',
  }
  return map[id] || 'thinking'
}

function normalizeBehaviorLabel(label) {
  const map = {
    'Implementing': 'Implementing',
    'Thinking': 'Thinking',
    'Debugging': 'Debugging',
    'Seeking Help': 'Seeking Help',
    'SeekingHelp': 'Seeking Help',
    'Testing': 'Testing',
    'Idle': 'Thinking',
  }
  return map[label] || label || 'Unknown'
}

// ─── Sequence Entropy ────────────────────────────────────────

function computeEntropy(segments) {
  if (segments.length <= 1) return 0
  const counts = {}
  segments.forEach(s => { const id = normalizeBehaviorId(s.id); counts[id] = (counts[id] || 0) + 1 })
  const total = segments.length
  let entropy = 0
  Object.values(counts).forEach(c => {
    const p = c / total
    if (p > 0) entropy -= p * Math.log2(p)
  })
  return Math.round(entropy * 1000) / 1000
}

// ─── Segment Parsing ─────────────────────────────────────────

function getSegmentsInWindow(analytics, startMs, endMs) {
  if (!analytics?.autoSegments) return []
  return analytics.autoSegments
    .filter(s => !s.isSystemEvent && s.behavior && s.startTime < endMs && s.endTime > startMs)
    .map(s => ({
      id: s.behavior?.id || 'unknown',
      label: s.behavior?.label || 'Unknown',
      duration: (Math.min(s.endTime, endMs) - Math.max(s.startTime, startMs)) / 1000,
      startTime: s.startTime,
      endTime: s.endTime,
    }))
}

function buildSequenceString(segments) {
  return segments.map(s => {
    const dur = s.duration
    const timeStr = dur >= 60 ? `${Math.floor(dur / 60)}m${Math.round(dur % 60)}s` : `${Math.round(dur)}s`
    return `${s.label.replace(/\s+/g, '')}(${timeStr})`
  }).join(' → ')
}

function buildSequenceParsed(segments) {
  if (segments.length === 0) return ''
  return segments.map(s => normalizeBehaviorLabel(s.label)).join(',')
}

function sumBehaviorTime(segments, behaviorId) {
  return segments
    .filter(s => normalizeBehaviorId(s.id) === behaviorId)
    .reduce((sum, s) => sum + s.duration, 0)
}

// ─── Window Metrics ──────────────────────────────────────────

function computeWindowMetrics(events, startTs, endTs, analytics, replayStart) {
  const w = events.filter(e => e.timestamp >= startTs && e.timestamp < endTs)
  const dur = (endTs - startTs) / 1000

  // Code activity
  const codeEdits = w.filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT').length
  const codeDeletes = w.filter(e => e.type === 'CODE_DELETE' || e.type === 'CODE_DELETE_SELECTION').length
  const codePastes = w.filter(e => e.type === 'CODE_PASTE').length
  const codeCopies = w.filter(e => e.type === 'CODE_COPY').length
  const codeUndos = w.filter(e => e.type === 'CODE_UNDO').length
  const codeEditRate = dur > 0 ? Math.round((codeEdits / dur) * 100) / 100 : 0

  const charsInserted = w.filter(e => e.type === 'CODE_TYPE').reduce((s, e) => s + (e.payload?.changes?.[0]?.text?.length || 0), 0)
  const charsDeleted = w.filter(e => e.type === 'CODE_DELETE' || e.type === 'CODE_DELETE_SELECTION').reduce((s, e) => s + (e.payload?.removed?.length || 1), 0)
  const netCodeGrowth = charsInserted - charsDeleted
  const deleteTypeRatio = codeEdits > 0 ? Math.round((codeDeletes / codeEdits) * 100) / 100 : 0

  // Terminal activity
  const runs = w.filter(e => e.type === 'TERMINAL_RUN')
  const errors = w.filter(e => e.type === 'TERMINAL_ERROR')
  const terminalRuns = runs.length
  const terminalErrors = errors.length

  let maxConsecErrors = 0, consecErrors = 0
  w.forEach(e => {
    if (e.type === 'TERMINAL_ERROR') { consecErrors++; maxConsecErrors = Math.max(maxConsecErrors, consecErrors) }
    else if (e.type === 'TERMINAL_RUN') consecErrors = 0
  })

  const runTimestamps = runs.map(e => e.timestamp)
  let meanTimeBetweenRuns = ''
  if (runTimestamps.length >= 2) {
    const gaps = []
    for (let i = 1; i < runTimestamps.length; i++) gaps.push((runTimestamps[i] - runTimestamps[i - 1]) / 1000)
    meanTimeBetweenRuns = Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length)
  }

  // Error response
  let errorReadingTime = '', errorToEdit = '', errorToQuery = '', errorToResolution = ''
  let errorSelfFix = 0, errorAiFix = 0
  errors.forEach(err => {
    const afterErr = w.filter(e => e.timestamp > err.timestamp)
    const nextEdit = afterErr.find(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT')
    const nextQuery = afterErr.find(e => e.type === 'CHAT_QUERY')
    const nextRun = afterErr.find(e => e.type === 'TERMINAL_RUN')

    if (nextEdit) errorToEdit = Math.round((nextEdit.timestamp - err.timestamp) / 1000)
    if (nextQuery) errorToQuery = Math.round((nextQuery.timestamp - err.timestamp) / 1000)

    if (nextEdit && nextRun && nextRun.timestamp > nextEdit.timestamp) {
      const errAfterRun = afterErr.find(e => e.type === 'TERMINAL_ERROR' && e.timestamp > nextRun.timestamp && e.timestamp < nextRun.timestamp + 2000)
      if (!errAfterRun) errorSelfFix++
      else errorAiFix++
    } else if (nextQuery && (!nextEdit || nextQuery.timestamp < nextEdit.timestamp)) {
      errorAiFix++
    }
  })

  // Failed test response
  const failedTests = w.filter(e => e.type === 'TEST_CASE_RESULT' && e.payload?.passed_count < e.payload?.total_tests)
  let failedTestCount = failedTests.length
  let failedTestReadingTime = '', failedTestToEdit = '', failedTestToQuery = ''
  let failedTestSelfFix = 0, failedTestAiFix = 0

  failedTests.forEach(ft => {
    const afterFt = w.filter(e => e.timestamp > ft.timestamp)
    const nextEdit = afterFt.find(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT')
    const nextQuery = afterFt.find(e => e.type === 'CHAT_QUERY')
    if (nextEdit) failedTestToEdit = Math.round((nextEdit.timestamp - ft.timestamp) / 1000)
    if (nextQuery) failedTestToQuery = Math.round((nextQuery.timestamp - ft.timestamp) / 1000)
    if (nextEdit && (!nextQuery || nextEdit.timestamp < nextQuery.timestamp)) failedTestSelfFix++
    else if (nextQuery) failedTestAiFix++
  })

  // Test state
  const allTestEvents = events.filter(e => e.type === 'TEST_CASE_RESULT' && e.timestamp <= endTs)
  const lastTestEvent = allTestEvents[allTestEvents.length - 1]
  const testLastPassed = lastTestEvent?.payload?.passed_count ?? ''
  const testLastTotal = lastTestEvent?.payload?.total_tests ?? ''
  const testPassCount = lastTestEvent?.payload?.passed_count ?? 0
  const testFailCount = (lastTestEvent?.payload?.total_tests ?? 0) - testPassCount

  // Prior AI interaction
  const chatResponses = w.filter(e => e.type === 'CHAT_RESPONSE')
  let responseReadingTime = '', chatToCodeLatency = ''
  if (chatResponses.length > 0) {
    const lastResponse = chatResponses[chatResponses.length - 1]
    const afterResponse = w.filter(e => e.timestamp > lastResponse.timestamp)
    const nextCode = afterResponse.find(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT')
    if (nextCode) chatToCodeLatency = Math.round((nextCode.timestamp - lastResponse.timestamp) / 1000)
    responseReadingTime = Math.round(dur)
  }

  // Tab / distraction
  const tabEvents = w.filter(e => e.type === 'TAB_STATE')
  const tabSwitches = tabEvents.length
  let tabHiddenTime = 0
  tabEvents.forEach((e, i) => {
    if (!e.payload?.visible && i < tabEvents.length - 1) {
      tabHiddenTime += (tabEvents[i + 1].timestamp - e.timestamp) / 1000
    }
  })

  // Region time (approximate from segments)
  const relStart = replayStart ? startTs - replayStart : 0
  const relEnd = replayStart ? endTs - replayStart : dur * 1000
  const windowSegs = getSegmentsInWindow(analytics, relStart, relEnd)
  const timeInEditor = sumBehaviorTime(windowSegs, 'implementing')
  const timeInChat = sumBehaviorTime(windowSegs, 'seeking_help')
  const timeInTerminal = sumBehaviorTime(windowSegs, 'testing') + sumBehaviorTime(windowSegs, 'debugging')
  const timeInTask = sumBehaviorTime(windowSegs, 'thinking')

  // Longest idle
  let longestIdle = 0
  for (let i = 1; i < w.length; i++) {
    const gap = (w[i].timestamp - w[i - 1].timestamp) / 1000
    longestIdle = Math.max(longestIdle, gap)
  }

  return {
    pre_code_edits: codeEdits, pre_code_deletes: codeDeletes, pre_code_pastes: codePastes,
    pre_code_copies: codeCopies, pre_code_undos: codeUndos, pre_code_edit_rate: codeEditRate,
    pre_chars_inserted: charsInserted, pre_chars_deleted: charsDeleted,
    pre_net_code_growth: netCodeGrowth, pre_delete_type_ratio: deleteTypeRatio,
    pre_terminal_runs: terminalRuns, pre_terminal_errors: terminalErrors,
    pre_max_consecutive_errors: maxConsecErrors, pre_mean_time_between_runs_s: meanTimeBetweenRuns,
    pre_error_reading_time_s: errorReadingTime, pre_error_to_edit_s: errorToEdit,
    pre_error_to_query_s: errorToQuery, pre_error_to_resolution_s: errorToResolution,
    pre_error_self_fix: errorSelfFix, pre_error_ai_fix: errorAiFix,
    pre_failed_test_count: failedTestCount,
    pre_failed_test_reading_time_s: failedTestReadingTime,
    pre_failed_test_to_edit_s: failedTestToEdit, pre_failed_test_to_query_s: failedTestToQuery,
    pre_failed_test_self_fix: failedTestSelfFix, pre_failed_test_ai_fix: failedTestAiFix,
    pre_test_pass_count: testPassCount, pre_test_fail_count: testFailCount,
    pre_test_last_passed: testLastPassed, pre_test_last_total: testLastTotal,
    pre_response_reading_time_s: responseReadingTime, pre_chat_to_code_latency_s: chatToCodeLatency,
    pre_tab_hidden_time_s: Math.round(tabHiddenTime), pre_tab_switches: tabSwitches,
    pre_time_in_editor_s: Math.round(timeInEditor), pre_time_in_chat_s: Math.round(timeInChat),
    pre_time_in_terminal_s: Math.round(timeInTerminal), pre_time_in_task_s: Math.round(timeInTask),
    pre_time_in_tests_s: '',
    pre_longest_idle_s: Math.round(longestIdle), pre_duration_s: Math.round(dur),
    _windowSegments: windowSegs,
  }
}

// ─── Behavioral Pattern (simple heuristic) ───────────────────

function classifyPattern(metrics, queryTimeSinceLastS) {
  if (metrics.pre_code_edits === 0 && metrics.pre_terminal_runs === 0 && queryTimeSinceLastS !== '' && queryTimeSinceLastS < 15)
    return 'rapid_reply'
  if (metrics.pre_code_edits === 0 && metrics.pre_terminal_runs === 0)
    return 'read_only'
  if (metrics.pre_code_edits > 0 && metrics.pre_terminal_runs === 0)
    return 'wrote_no_test'
  if (metrics.pre_terminal_runs > 0)
    return 'debug_test_no_new_code'
  return 'read_only'
}

// ─── Main Builder ────────────────────────────────────────────

export async function buildQueryDataset(students, eventsByUser, sessionId, analyticsById, deployment = '') {
  const rows = []

  for (const student of students) {
    const events = eventsByUser[student.id] || eventsByUser[String(student.id)] || []
    if (events.length === 0) continue

    const analytics = analyticsById?.[student.id]?.analytics || analyticsById?.[String(student.id)]?.analytics || null
    const name = student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown'
    const replayStart = analytics?.replayStartTime || events[0].timestamp

    // DB messages
    let dbMessages = []
    try {
      dbMessages = await getMessagesBetweenUserAndAI(student.id, sessionId)
    } catch (err) {
      console.error(`Failed to fetch messages for ${name}:`, err)
      continue
    }

    const queryEvents = events.filter(e => e.type === 'CHAT_QUERY').sort((a, b) => a.timestamp - b.timestamp)
    if (queryEvents.length === 0) continue

    const sessionStart = events[0].timestamp
    const sessionEnd = events[events.length - 1].timestamp
    const sessionDuration = Math.round((sessionEnd - sessionStart) / 1000)

    // Test outcome
    const allTestEvents = events.filter(e => e.type === 'TEST_CASE_RESULT')
    const lastTestEver = allTestEvents[allTestEvents.length - 1]
    const finalPassed = lastTestEver?.payload?.passed_count ?? ''
    const finalTotal = lastTestEver?.payload?.total_tests ?? ''
    const taskCompleted = (finalTotal > 0 && finalPassed === finalTotal) ? 1 : 0

    // Demographics
    const demo = student.demographic || {}

    // AI messages from DB
    const aiMessages = dbMessages.filter(m => m.sender_id === AI_AGENT_ID)
    const userMessages = dbMessages.filter(m => m.sender_id !== AI_AGENT_ID)

    for (let qIdx = 0; qIdx < queryEvents.length; qIdx++) {
      const qEvent = queryEvents[qIdx]
      const qTs = qEvent.timestamp
      const isFirst = qIdx === 0
      const isLast = qIdx === queryEvents.length - 1
      const prevQ = qIdx > 0 ? queryEvents[qIdx - 1] : null

      // Window boundaries
      const prevAi = aiMessages.filter(m => new Date(m.created_time).getTime() < qTs)
      const windowStart = prevAi.length > 0
        ? new Date(prevAi[prevAi.length - 1].created_time).getTime()
        : sessionStart
      const hasPriorAi = prevAi.length > 0

      const timeSinceStart = Math.round((qTs - sessionStart) / 1000)
      const timeSinceLastQuery = prevQ ? Math.round((qTs - prevQ.timestamp) / 1000) : ''
      const segmentDuration = Math.round((qTs - windowStart) / 1000)

      // Pre-query metrics
      const preMetrics = computeWindowMetrics(events, windowStart, qTs, analytics, replayStart)
      const windowSegs = preMetrics._windowSegments
      delete preMetrics._windowSegments

      // Pre-query sequence — human-readable with durations
      const preSequence = buildSequenceString(windowSegs)
      // Pre-query sequence — comma-delimited labels for programmatic bigram extraction
      const preSequenceParsed = buildSequenceParsed(windowSegs)
      const preEntropy = computeEntropy(windowSegs)

      // Behavioral profile
      const profile = classifyProfile(
        preMetrics.pre_code_edits + (preMetrics.pre_code_deletes || 0),
        preMetrics.pre_terminal_runs,
        preMetrics.pre_terminal_errors,
        hasPriorAi,
      )

      // Behavioral pattern
      const pattern = classifyPattern(preMetrics, timeSinceLastQuery)

      // Query content
      const queryText = qEvent.payload?.text || qEvent.payload?.message || qEvent.payload?.content || ''
      const queryLenChars = queryText.length
      const queryLenWords = queryText.split(/\s+/).filter(Boolean).length

      // Duplicate detection — same query text as previous within 5s, or empty content
      const prevQueryText = prevQ ? (prevQ.payload?.text || prevQ.payload?.message || prevQ.payload?.content || '') : ''
      const isDuplicate = (queryText === '' || queryText === null)
        ? true
        : (prevQ
          ? (queryText === prevQueryText && timeSinceLastQuery !== '' && timeSinceLastQuery < 5)
          : false)

      // AI response
      const dbQueryMsg = userMessages[qIdx]
      const aiResponse = dbQueryMsg
        ? dbMessages.find(m => m.sender_id === AI_AGENT_ID && new Date(m.created_time).getTime() > new Date(dbQueryMsg.created_time).getTime())
        : null
      const aiContent = aiResponse?.content || ''
      const aiLenChars = aiContent.length
      const aiLenWords = aiContent.split(/\s+/).filter(Boolean).length

      // Post-query window
      const nextQ = qIdx < queryEvents.length - 1 ? queryEvents[qIdx + 1] : null
      const postEnd = nextQ ? nextQ.timestamp : sessionEnd
      const aiResponseTs = aiResponse ? new Date(aiResponse.created_time).getTime() : qTs + 2000
      const postStart = aiResponseTs

      const postMetrics = computeWindowMetrics(events, postStart, postEnd, analytics, replayStart)
      const postSegs = postMetrics._windowSegments
      delete postMetrics._windowSegments
      const postSequence = buildSequenceString(postSegs)
      const postSequenceParsed = buildSequenceParsed(postSegs)
      const postEntropy = computeEntropy(postSegs)

      // Test state at query
      const testAtQuery = allTestEvents.filter(e => e.timestamp <= qTs)
      const lastTestAtQ = testAtQuery[testAtQuery.length - 1]
      const testPassedAtQuery = lastTestAtQ?.payload?.passed_count ?? ''
      const testTotalAtQuery = lastTestAtQ?.payload?.total_tests ?? ''

      // Test delta
      const testPassedPrev = prevQ
        ? (allTestEvents.filter(e => e.timestamp <= prevQ.timestamp).pop()?.payload?.passed_count ?? '')
        : ''
      const testDelta = (testPassedAtQuery !== '' && testPassedPrev !== '')
        ? testPassedAtQuery - testPassedPrev : ''

      // Code at first query
      const codeAtFirst = isFirst ? (dbMessages.find(m => m.sender_id !== AI_AGENT_ID)?.code_context || '') : ''

      // Rename post metrics keys
      const postRenamed = {}
      Object.entries(postMetrics).forEach(([k, v]) => {
        postRenamed[k.replace('pre_', 'post_')] = v
      })

      rows.push({
        student_id: student.id,
        student_name: name,
        session_id: sessionId,
        deployment,
        query_index: qIdx + 1,
        is_first_query: isFirst ? 1 : 0,
        is_last_query: isLast ? 1 : 0,
        has_prior_ai_response: hasPriorAi ? 1 : 0,
        is_duplicate: isDuplicate ? 1 : 0,
        experiment_group: student.experiment_group || '',
        // Timing
        time_since_session_start_s: timeSinceStart,
        time_since_last_query_s: timeSinceLastQuery,
        segment_duration_s: segmentDuration,
        // Demographics
        demo_gender: demo.gender || '',
        demo_year: demo.year || '',
        demo_major: demo.major || '',
        demo_python_experience: demo.python_experience || '',
        demo_llm_usage: demo.llm_usage || '',
        // Pre-query sequence
        behavior_sequence: preSequence,
        behavior_sequence_parsed: preSequenceParsed,
        implementing_time_s: Math.round(sumBehaviorTime(windowSegs, 'implementing')),
        debugging_time_s: Math.round(sumBehaviorTime(windowSegs, 'debugging')),
        thinking_time_s: Math.round(sumBehaviorTime(windowSegs, 'thinking')),
        seeking_help_time_s: Math.round(sumBehaviorTime(windowSegs, 'seeking_help')),
        testing_time_s: Math.round(sumBehaviorTime(windowSegs, 'testing')),
        // Pre-query metrics
        ...preMetrics,
        // Query
        query_content: queryText,
        query_length_chars: queryLenChars,
        query_length_words: queryLenWords,
        query_type: '',
        // AI response
        ai_response_content: aiContent,
        ai_response_length_chars: aiLenChars,
        ai_response_length_words: aiLenWords,
        // Post-query
        post_response_sequence: postSequence,
        post_response_sequence_parsed: postSequenceParsed,
        post_response_implementing_s: Math.round(sumBehaviorTime(postSegs, 'implementing')),
        post_response_debugging_s: Math.round(sumBehaviorTime(postSegs, 'debugging')),
        post_response_thinking_s: Math.round(sumBehaviorTime(postSegs, 'thinking')),
        post_response_seeking_help_s: Math.round(sumBehaviorTime(postSegs, 'seeking_help')),
        post_response_testing_s: Math.round(sumBehaviorTime(postSegs, 'testing')),
        ...postRenamed,
        // Test state
        test_passed_at_query: testPassedAtQuery,
        test_total_at_query: testTotalAtQuery,
        test_passed_prev_query: testPassedPrev,
        test_delta: testDelta,
        code_at_first_query: isFirst ? codeAtFirst : '',
        // Derived
        behavioral_pattern: pattern,
        behavioral_profile: profile,
        pre_entropy: preEntropy,
        post_entropy: postEntropy,
        final_test_passed: finalPassed,
        final_test_total: finalTotal,
        task_completed: taskCompleted,
        session_duration_s: sessionDuration,
      })
    }
  }

  return rows
}

// ─── CSV Export ───────────────────────────────────────────────

export function downloadQueryCSV(rows, filename) {
  if (rows.length === 0) return
  const headers = Object.keys(rows[0])
  const csv = [
    headers.join(','),
    ...rows.map(row => headers.map(h => {
      const v = row[h]
      if (typeof v === 'string' && (v.includes(',') || v.includes('"') || v.includes('\n'))) return esc(v)
      return v ?? ''
    }).join(','))
  ].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}